// FIVE SECONDS TO TAKE BACK "בוצע". A task marked done leaves the board at once (the
// board shows open work only), so a mis-click would otherwise vanish with nothing to
// undo it. One quiet line at the foot of the screen says it was done and offers ביטול,
// which reopens the task. A second "בוצע" within the five seconds replaces the line: the
// newest one is the one that can still be taken back.
//
// A provider per screen (the work board, the leads screen). TaskRow asks for it and
// renders fine without one, so a task row anywhere else keeps working as before.
import { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import he from '../i18n/he';
import { tasksApi } from '../api';
import { useAlerts } from '../context/AlertsContext';

const UNDO_MS = 5000;
const DoneUndoContext = createContext(null);

export const useDoneUndo = () => useContext(DoneUndoContext);

// `onDone(task)` runs the moment a task is marked done (the screen holds its group in
// place); `onUndone()` after ביטול reopened it (the screen reloads).
export function DoneUndoProvider({ onDone, onUndone, children }) {
  const [offered, setOffered] = useState(null); // the task that can still be taken back
  const timer = useRef(null);
  const alerts = useAlerts();

  const clear = useCallback(() => { clearTimeout(timer.current); setOffered(null); }, []);
  useEffect(() => () => clearTimeout(timer.current), []);

  const offer = useCallback((task) => {
    clearTimeout(timer.current);
    onDone?.(task);
    setOffered(task);
    timer.current = setTimeout(() => setOffered(null), UNDO_MS);
  }, [onDone]);

  const undo = async () => {
    const task = offered;
    clear();
    await tasksApi.setStatus(task.id, 'open');
    alerts?.reload();
    onUndone?.();
  };

  return (
    <DoneUndoContext.Provider value={offer}>
      {children}
      {offered && (
        <div className="done-undo" role="status">
          <span>{he.tasks.markedDone}</span>
          <button type="button" onClick={undo}>{he.tasks.undo}</button>
        </div>
      )}
    </DoneUndoContext.Provider>
  );
}
